// Faithful port of the AngularJS ScheduleService weekly-schedule parser
// (static/js/services/scheduleservice.js). Behavior is pinned by a golden-master
// characterization suite (scheduleUtils.test.ts) captured from the real service.
//
// Contract that must stay byte-identical: for a VALID week, parseWeeklySchedule
// produces the same per-day military_times the backend expects (including the
// overnight carry into the next day, and Sunday's carry wrapping to Monday).
// For invalid input it reports userScheduleIsValid=false with the same per-day
// error messages. Two deliberate, documented divergences from the legacy code,
// both on non-persisted/guarded paths: an unparseable ("garbage") input yields
// valid=false with empty military_times instead of the legacy null/NaN arrays,
// and an empty day input yields valid=false instead of throwing (the legacy code
// crashed on null.split; the Angular UI blocks empty days before this runs).

export type MilitaryRange = [number, number];

export interface DaySchedule {
  customer_input: string;
  military_times: MilitaryRange[];
}

export type WeeklySchedule = Record<string, DaySchedule>;

export interface ParseResult {
  userScheduleIsValid: boolean;
  scheduleAsMilitaryTime: WeeklySchedule;
  errors: Record<string, string>;
}

export const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const TOMORROW: Record<string, string> = {
  monday: "tuesday",
  tuesday: "wednesday",
  wednesday: "thursday",
  thursday: "friday",
  friday: "saturday",
  saturday: "sunday",
  sunday: "monday",
};

const toMilitaryTime = (timeString: string | undefined): number | null => {
  const match = /^(\d\d?):?(\d\d)?(AM|PM)$/.exec(timeString ?? "");
  if (!match) return null;
  const [, hourString, minuteRaw, meridian] = match;
  const minuteString = minuteRaw || "00";
  let hour = parseInt(hourString, 10);
  if (hour === 12) hour = 0;
  return 1200 * (meridian === "PM" ? 1 : 0) + hour * 100 + parseInt(minuteString, 10);
};

const parseTimeRange = (rangeString: string): [number | null, number | null] => {
  const [t0str, t1str] = rangeString.split("-");
  const t0 = toMilitaryTime(t0str);
  let t1 = toMilitaryTime(t1str);
  if (t1 === 0) t1 = 2400;
  return [t0, t1];
};

const parseTimeRangeList = (rangeList: string[]): Array<[number | null, number | null]> =>
  rangeList
    .filter((range) => range && range.length)
    .map((range) => {
      const [t0, t1] = parseTimeRange(range);
      if (t0 !== null && t1 !== null && t0 <= t1) return [t0, t1];
      // Overnight (t0 > t1) pushes the end into the next day; invalid endpoints fall through as null.
      return [t0, t1 === null ? null : t1 + 2400];
    });

const cleanInput = (input: string | null | undefined): string | null => {
  if (!input) return null;
  return input
    .replace(/[\s.]/g, "")
    .toUpperCase()
    .replace("CLOSED", "")
    .replace("MIDNIGHT", "12AM")
    .replace("OPEN", "12AM-12AM");
};

const rangesOverlap = (block1: MilitaryRange, block2: MilitaryRange): boolean => {
  const [t0, t1] = block1;
  const [t2, t3] = block2;
  if (t0 <= t2 && t1 >= t2 && t1 <= t3) return true;
  if (t2 <= t0 && t3 >= t0 && t3 <= t1) return true;
  if ((t0 <= t2 && t3 <= t1) || (t2 <= t0 && t1 <= t3)) return true;
  return false;
};

const validateRanges = (currentDayBlocks: MilitaryRange[], nextDayBlocks: MilitaryRange[]): string | null => {
  const numCurrent = currentDayBlocks.length;
  const numNext = nextDayBlocks.length;

  for (let i = 0; i < numCurrent; i++) {
    let [t0, t1] = currentDayBlocks[i];
    if (t1 <= t0) t1 = t1 + 2400;

    if (numCurrent > 1) {
      for (let b = i + 1; b < numCurrent; b++) {
        let [t2, t3] = currentDayBlocks[b];
        if (t3 <= t2) t3 = t3 + 2400;
        if (rangesOverlap([t0, t1], [t2, t3])) return "Overlapping time ranges.";
      }
    }

    if (t1 > 2400 && numNext) {
      const overlapBlock: MilitaryRange = [0, t1 - 2400];
      for (let c = 0; c < numNext; c++) {
        let [n0, n1] = nextDayBlocks[c];
        if (n1 <= n0) n1 = n1 + 2400;
        if (rangesOverlap(overlapBlock, [n0, n1])) return "Overlapping time ranges with time ranges for next day.";
      }
    }
  }

  return null;
};

const numericRanges = (ranges: Array<[number | null, number | null]>): MilitaryRange[] =>
  ranges.filter((range): range is MilitaryRange => range[0] !== null && range[1] !== null);

// Takes a weekly schedule keyed monday..sunday with each day's `customer_input`,
// and returns a fresh schedule with `military_times` populated per day, a validity
// flag, and per-day error messages. Never mutates the input.
export const parseWeeklySchedule = (input: WeeklySchedule): ParseResult => {
  const schedule: WeeklySchedule = {};
  for (const day of DAY_ORDER) {
    schedule[day] = { customer_input: input[day]?.customer_input ?? "", military_times: [] };
  }

  let valid = true;
  const errors: Record<string, string> = {};

  for (const day of DAY_ORDER) {
    const tomorrow = TOMORROW[day];
    const tomorrowInput = schedule[tomorrow]?.customer_input;

    // The next day's input is required; it participates in overnight-carry validation.
    if (!tomorrowInput) {
      errors[tomorrow] = "Input required.";
      valid = false;
      break;
    }

    const cleanCurrent = cleanInput(schedule[day].customer_input);
    if (cleanCurrent === null) {
      errors[day] = "Input required.";
      valid = false;
      continue;
    }

    const currentRanges = parseTimeRangeList(cleanCurrent.split(","));
    const tomorrowRanges = parseTimeRangeList((cleanInput(tomorrowInput) ?? "").split(","));

    const overlapError = validateRanges(numericRanges(currentRanges), numericRanges(tomorrowRanges));
    if (overlapError) {
      errors[day] = overlapError;
      valid = false;
    }

    for (const [t0, t1] of currentRanges) {
      if (t0 === null || t1 === null) {
        valid = false;
        continue;
      }
      if (t1 <= 2400) {
        schedule[day].military_times.push([t0, t1]);
      } else {
        schedule[day].military_times.push([t0, 2400]);
        schedule[tomorrow].military_times.push([0, t1 - 2400]);
      }
    }
  }

  return { userScheduleIsValid: valid, scheduleAsMilitaryTime: schedule, errors };
};

// The legacy `hoursformatter` directive: max_order_ahead_minutes is edited in hours.
export const minutesToHours = (minutes: number): number => Math.round(minutes / 60);
export const hoursToMinutes = (hours: number): number => hours * 60;
